import {DateTime} from 'luxon';

import {AnalyticsJobStatusEnum} from '../../../libs/shared/core/src/models/api/cellsAnalytics';
import {AnalyticsMetricsKeys} from '../../models/api/analyticsMetricsModel';
import {
  AnalyticsFilterRelationsEnum,
  AnalyticsFilterTypesEnum,
} from '../../models/api/analyticsSavedViewModel';
import {DismissedAnalyticsInfoBanner} from '../../models/api/localPreferencesModel';
import {AppState} from '../../store/appState';
import {getInboxes} from '../../store/data/inboxes/inboxesSelectors';
import {getDismissedAnalyticsInfoBanner} from '../../store/data/localPreferences/localPreferencesSelectors';
import {
  InboxesConversationsParameters,
  isInboxesInboxParameters,
} from '../../store/panels/inboxes/parameters/inboxesParameters';
import {AnalyticsReportQueryParams, ReportFilterOptionsEnum} from './reportHelpers/reportTypes';

// only show the banner the first "workday" per week of a teammate
const AUTO_DISMISS_THRESHOLD = 20 * 60 * 60 * 1000; // 20 hours

export function shouldDismissCellsAnalyticsInfoBanner(
  analyticsInfoBannerDismissal: DismissedAnalyticsInfoBanner | undefined,
) {
  const reportDate = getCellsAnalyticsReportUpdateDate();

  const isLastDismissedDateMoreRecentThanLastReport =
    analyticsInfoBannerDismissal?.lastDismissedDate &&
    analyticsInfoBannerDismissal.lastDismissedDate > reportDate.toMillis();

  const isLastSeenAtDateMoreThanThreshold =
    analyticsInfoBannerDismissal?.seenAtDate &&
    analyticsInfoBannerDismissal.seenAtDate > reportDate.toMillis() &&
    analyticsInfoBannerDismissal.seenAtDate + AUTO_DISMISS_THRESHOLD < Date.now();

  const neverShowAgain = analyticsInfoBannerDismissal?.neverShowAgainDate;

  return Boolean(
    neverShowAgain || isLastDismissedDateMoreRecentThanLastReport || isLastSeenAtDateMoreThanThreshold,
  );
}

// Returns the date of the last Monday at 8am.
export function getCellsAnalyticsReportUpdateDate(fromDate: DateTime<true> = DateTime.now()) {
  // luxon doesn't respect local start of week, "startOf('week')" is always monday https://github.com/moment/luxon/issues/373

  // If we are between Monday 12am and Monday 8am, we need to return last week's Monday 8am.
  // (because in that case, the new week hasn't started yet).
  // Starting by subtracting 8 hours works around this by making sure we are always in the correct week.
  return fromDate.minus({hours: 8}).startOf('week').plus({hours: 8});
}

export function getCellsAnalyticsReportStartDate(endDate: DateTime<true>) {
  return endDate.minus({weeks: 1});
}

export function getCellsAnalyticsReportQueryParams(
  timezone: string,
  inboxId: number,
  metrics: ReadonlyArray<AnalyticsMetricsKeys>,
  reportDate: DateTime<true>,
): AnalyticsReportQueryParams {
  // we always want a one-week span
  const oneWeekBefore = reportDate.minus({weeks: 1});

  return {
    metrics,
    period: {
      start: oneWeekBefore.toISO(),
      end: reportDate.toISO(),
      tz: timezone,
    },
    filters: {
      terms: [
        {
          type: AnalyticsFilterTypesEnum.INBOXES,
          ids: [inboxId],
          relation: AnalyticsFilterRelationsEnum.OR,
          negated: false,
        },
      ],
      options: {
        [ReportFilterOptionsEnum.PREVIOUS_PERIOD_METRICS]: metrics,
      },
    },
  };
}

export function getCellsAnalyticsReportFetchContext(
  parameters: InboxesConversationsParameters,
  state: AppState,
) {
  if (!isInboxesInboxParameters(parameters)) {
    return undefined;
  }

  const dismissedAnalyticsInfoBanner = getDismissedAnalyticsInfoBanner(state);

  const shouldDismiss = shouldDismissCellsAnalyticsInfoBanner(dismissedAnalyticsInfoBanner);
  if (shouldDismiss) {
    return undefined;
  }

  const inboxes = getInboxes(state);
  const {inboxId} = parameters;
  const inbox = inboxes[inboxId];
  if (!inbox) {
    return undefined;
  }

  const {cellsAnalytics, namespace} = inbox;
  // Report is already available in store
  if (cellsAnalytics?.status === AnalyticsJobStatusEnum.DONE) {
    return undefined;
  }

  const reportDate = cellsAnalytics?.reportDate ? DateTime.fromISO(cellsAnalytics.reportDate) : undefined;

  return {
    inboxId,
    namespace,
    reportDate: reportDate?.isValid ? reportDate : undefined,
    jobUid: cellsAnalytics?.jobUid,
  };
}
